import {
  GUEST_CARD_MIN_WIDTH,
  type GuestListColumnCount,
} from '@/modules/guest-web/constants/guestLayout';

/**
 * ADHA home listing layout — mirrors guest project-details split.
 * Phone < 768, tablet 768–1144 (tabs), desktop ≥ 1145 (side-by-side).
 *
 * Side-by-side starts at 1145 (not 1100): 1100–1145 is too narrow for the
 * status card + project hero split and caused the image to overlap the card.
 *
 * Desktop column ratio (Figma): left 729 / right 442 (~62% : 38%, ≈ 5:3), gap 24.
 */
export const ADHA_LISTING_PHONE_MAX = 768;
export const ADHA_LISTING_TABLET_MAX = 1145;
/** Figma left (project) column width — used as flexGrow ratio with SIDEBAR. */
export const ADHA_LISTING_MAIN_FLEX = 729;
/** Figma right (Explore Units) column width — used as flexGrow ratio with MAIN. */
export const ADHA_LISTING_SIDEBAR_FLEX = 442;

/** Horizontal inset already applied by WebAppLayout `shellMobile` (16). */
export const ADHA_LISTING_COMPACT_PADDING_X = 16;
/** Horizontal/vertical gap between unit cards in the Explore grid. */
export const ADHA_UNITS_GRID_GAP = 16;

export type AdhaListingTab = 'project' | 'units';

export interface AdhaListingLayout {
  readonly isPhone: boolean;
  readonly isTablet: boolean;
  /** Phone + tablet: Project | Units tabs; desktop: side-by-side. */
  readonly isCompact: boolean;
  readonly carouselHeight: number;
}

function resolveCarouselHeight(isPhone: boolean, isTablet: boolean): number {
  if (isPhone) {
    return 228;
  }
  if (isTablet) {
    return 320;
  }
  return 421;
}

export function getAdhaListingLayout(width: number): AdhaListingLayout {
  const isPhone = width < ADHA_LISTING_PHONE_MAX;
  const isTablet =
    width >= ADHA_LISTING_PHONE_MAX && width < ADHA_LISTING_TABLET_MAX;
  return {
    isPhone,
    isTablet,
    isCompact: isPhone || isTablet,
    carouselHeight: resolveCarouselHeight(isPhone, isTablet),
  };
}

/**
 * Same as guest catalogue: prefer more columns while each stays ≥ card min width.
 * Steps 4 → 3 → 2 → 1.
 */
function resolveUnitsColumnCount(contentWidth: number): GuestListColumnCount {
  if (contentWidth >= GUEST_CARD_MIN_WIDTH * 4) {
    return 4;
  }
  if (contentWidth >= GUEST_CARD_MIN_WIDTH * 3) {
    return 3;
  }
  if (contentWidth >= GUEST_CARD_MIN_WIDTH * 2) {
    return 2;
  }
  return 1;
}

export function getAdhaUnitsColumnCount(
  contentWidth: number,
): GuestListColumnCount {
  return resolveUnitsColumnCount(contentWidth);
}

/**
 * Pixel column width so cards fill the measured grid and align with the filter.
 * `gap` is shared between columns (not applied outside the outer edges).
 */
export function getAdhaUnitsColumnWidthPx(
  gridWidth: number,
  columnCount: GuestListColumnCount,
  gap: number = ADHA_UNITS_GRID_GAP,
): number {
  if (gridWidth <= 0) {
    return 0;
  }
  if (columnCount <= 1) {
    return gridWidth;
  }
  const totalGap = gap * (columnCount - 1);
  return Math.max(0, Math.floor((gridWidth - totalGap) / columnCount));
}
